import { useCallback, useEffect, useRef, useState } from 'react';
import { api } from '@/lib/apiClient';

export interface PlacePrediction {
  place_id: string;
  description: string;
  structured_formatting: {
    main_text: string;
    secondary_text: string;
  };
}

export interface ParsedPlace {
  placeId: string;
  formattedAddress: string;
  latitude?: number;
  longitude?: number;
}

const DEBOUNCE_MS = 200;

export function usePlacesAutocomplete() {
  const [predictions, setPredictions] = useState<PlacePrediction[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const [isFetchingDetails, setIsFetchingDetails] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const abortRef = useRef<AbortController | null>(null);

  const fetchPredictions = useCallback(async (input: string) => {
    if (!input || input.length < 3) {
      setPredictions([]);
      return;
    }

    abortRef.current?.abort();
    const controller = new AbortController();
    abortRef.current = controller;

    setIsSearching(true);
    setError(null);
    try {
      const response = await api.get<{ success: boolean; data: PlacePrediction[] }>(
        `/places/autocomplete?q=${encodeURIComponent(input)}`,
        { signal: controller.signal }
      );
      if (controller.signal.aborted) return;
      setPredictions(response.data ?? []);
    } catch (err: unknown) {
      if (controller.signal.aborted) return;
      const name = err && typeof err === 'object' && 'name' in err ? String(err.name) : '';
      const code = err && typeof err === 'object' && 'code' in err ? String(err.code) : '';
      if (name === 'CanceledError' || code === 'ERR_CANCELED') return;
      setError('Could not search addresses');
      setPredictions([]);
    } finally {
      if (!controller.signal.aborted) setIsSearching(false);
    }
  }, []);

  const handleSearchChange = useCallback(
    (text: string) => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
      if (text.length < 3) {
        setPredictions([]);
        return;
      }
      debounceRef.current = setTimeout(() => {
        fetchPredictions(text);
      }, DEBOUNCE_MS);
    },
    [fetchPredictions]
  );

  const selectPrediction = useCallback(async (prediction: PlacePrediction): Promise<ParsedPlace | null> => {
    setIsFetchingDetails(true);
    setError(null);
    try {
      const response = await api.get<{
        success: boolean;
        data: ParsedPlace;
      }>(`/places/details?placeId=${encodeURIComponent(prediction.place_id)}`);
      setPredictions([]);
      return response.data ?? null;
    } catch {
      setError('Could not get address details');
      return null;
    } finally {
      setIsFetchingDetails(false);
    }
  }, []);

  useEffect(() => {
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
      abortRef.current?.abort();
    };
  }, []);

  return {
    predictions,
    isSearching,
    isFetchingDetails,
    error,
    hasApiKey: true,
    handleSearchChange,
    selectPrediction,
    clearPredictions: () => setPredictions([]),
  };
}
